import { useMemo } from "react";

import { useGetInfiniteConversations } from "@/api/agenticAI/useConversationsApi";
import type { ChatSearchRow } from "@/components/agenticAI/constants";
import { timeAgo } from "@/helpers/time";
import { CHAT_SEARCH_MIN_CHARS } from "@/utils/recentChatSearches";

import { useDebounce } from "../useDebounce";

export const CHAT_SEARCH_DEBOUNCE_MS = 500;

export const useChatSearch = (input: string) => {
  const trimmed = input.trim();
  const debounced = useDebounce(trimmed, CHAT_SEARCH_DEBOUNCE_MS);
  const isLongEnough = trimmed.length >= CHAT_SEARCH_MIN_CHARS;
  const query =
    isLongEnough && debounced.length >= CHAT_SEARCH_MIN_CHARS ? debounced : "";
  const isActive = query !== "";

  const {
    data,
    isPending,
    isError,
    isFetchNextPageError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch
  } = useGetInfiniteConversations({ q: query }, { enabled: isActive });

  const results = useMemo<ChatSearchRow[]>(
    () =>
      isActive
        ? (data?.pages ?? []).flatMap(({ conversations }) =>
            conversations.map(({ thread_id, title, preview, updated_at }) => ({
              id: thread_id,
              title,
              preview,
              timeLabel: timeAgo(updated_at)
            }))
          )
        : [],
    [data, isActive]
  );
  const firstPage = isActive ? data?.pages[0] : undefined;

  return {
    query,
    isLoading:
      isLongEnough &&
      results.length === 0 &&
      (debounced !== trimmed || (isActive && isPending)),
    results,
    total: firstPage?.total,
    isTotalExact: firstPage?.total_is_exact ?? true,
    isInitialError: isActive && isError && results.length === 0,
    isLoadMoreError: isActive && isFetchNextPageError,
    hasNextPage: isActive && hasNextPage,
    isFetchingNextPage: isActive && isFetchingNextPage,
    fetchNextPage,
    refetch
  };
};
